import { useEffect } from 'react';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import ServicesSection from './components/ServicesSection';
import WhyChooseSection from './components/WhyChooseSection';
import BeforeAfterSection from './components/BeforeAfterSection';
import AreasServedSection from './components/AreasServedSection';
import ContactSection from './components/ContactSection';
import FooterSection from './components/FooterSection';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const LOCAL_BUSINESS_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': ['LocalBusiness', 'GeneralContractor'],
      '@id': `${SITE_URL}/#business`,
      name: 'Butler Frelix Construction LLC',
      alternateName: 'Butler Frelix',
      description:
        'Chicago\'s trusted general contractor for 11+ years. Expert HVAC, plumbing, electrical, roofing, remodeling, and commercial buildouts. Licensed & insured. Serving all of Chicagoland. Free quotes available.',
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/6645531746f64686987a03e27da8ae74/16f64b1b2195676dda194d0cc4a4f313.jpeg',
        width: 200,
        height: 200,
      },
      image:
        'https://public.readdy.ai/ai/img_res/9f6af27b89b4d7c0a5845f3d6f9de963.jpg',
      telephone: '+1-312-399-0278',
      email: 'build@bfclegacy.com',
      priceRange: '$$',
      foundingDate: '2013',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Chicago',
        addressRegion: 'IL',
        addressCountry: 'US',
        description: 'Serving Chicago, IL & Greater Chicagoland Area',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 41.8781,
        longitude: -87.6298,
      },
      areaServed: [
        { '@type': 'City', name: 'Chicago', containedInPlace: { '@type': 'State', name: 'Illinois' } },
        { '@type': 'AdministrativeArea', name: 'Chicagoland' },
      ],
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          opens: '07:00',
          closes: '19:00',
        },
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Construction & Contracting Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'HVAC', description: 'Reliable HVAC installation, repair, and replacement for homes and businesses across Chicago. Year-round comfort solutions.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Plumbing', description: 'From leaky pipes to full system installations — done right the first time. No repeat visits, no surprise costs.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Electrical', description: 'Professional electrical services from panel upgrades to full buildouts. Every job meets code and keeps your property safe.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Porches & Decks', description: 'Custom porches and decks built to hold up against Chicago weather for years. We add real value to your outdoor space.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Roofing & Siding', description: 'Installation, repair, and replacement using quality materials. We assess honestly and handle quickly.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Painting', description: 'Interior and exterior painting with clean, professional results. From prep to final coat, every surface is treated with care.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Remodeling & Renovations', description: '11 years of Chicago remodeling experience. Better layouts, better finishes, better function — we manage every detail.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Construction Management', description: 'We oversee every phase — planning, permitting, final walkthrough — keeping projects on schedule and on budget.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Commercial Buildouts', description: 'Professional buildouts for small businesses throughout Chicago and the suburbs, delivered on time and within budget.' } },
        ],
      },
      sameAs: [],
    },
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      url: SITE_URL,
      name: 'Butler Frelix Construction LLC',
      description: 'Chicago general contractor — HVAC, plumbing, electrical, roofing, remodeling & commercial buildouts.',
      publisher: { '@id': `${SITE_URL}/#business` },
      inLanguage: 'en-US',
    },
    {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/#webpage`,
      url: SITE_URL,
      name: 'Chicago General Contractor | Butler Frelix Construction LLC — Where Every Build Becomes a Legacy',
      description:
        'Butler Frelix Construction LLC — Chicago\'s trusted general contractor for 11+ years. Expert HVAC, plumbing, electrical, roofing, remodeling, and commercial buildouts. Licensed & insured. Serving all of Chicagoland. Free quotes available.',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': `${SITE_URL}/#business` },
      dateModified: '2026-03-31',
      inLanguage: 'en-US',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL }],
      },
    },
  ],
};

export default function HomePage() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-local-business';
    script.textContent = JSON.stringify(LOCAL_BUSINESS_SCHEMA);
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById('schema-local-business');
      if (existing) existing.remove();
    };
  }, []);

  return (
    <main className="w-full min-h-screen">
      <Navbar />
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <WhyChooseSection />
      <BeforeAfterSection />
      <AreasServedSection />
      <ContactSection />
      <FooterSection />
    </main>
  );
}
